import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Typography,
  Tabs,
  TabsHeader,
  TabsBody,
  Tab,
  TabPanel,
  Collapse,
} from "@material-tailwind/react";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import Label from "components/Label/Label";

interface DeleteModalProps {
  open: boolean;
  handleClose?: () => void;
  clientData: {
    id?: number;
    name?: string;
    lastName?: string;
    dateOfBirth?: string;
    phone?: string;
    validation?: string;
    enable?: boolean;
    createdAt?: string;
    updatedAt?: string;
    userId?: number;
    country?: string | null;
    city?: string | null;
    line1?: string | null;
    rue?: string | null;
    postal_code?: string | null;
    User?: {
      id?: number;
      email?: string;
      role?: string;
      last_connection?: string | null;
      emailVerified?: boolean; // Corrected from emailVirefied
      enable?: boolean;
      subscription_status?: string;
      createdAt?: string;
    };
  } | null;
}

const ClientDetailsPage: React.FC<DeleteModalProps> = ({ open, handleClose, clientData }) => {
  const [activeTab, setActiveTab] = useState("basic");
  const [openCollapse, setOpenCollapse] = useState({
    address: false,
  });

  // Toggle collapse sections
  const toggleCollapse = (section: keyof typeof openCollapse) => {
    setOpenCollapse((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  // Field icons using Line Awesome
  const fieldIcons = {
    name: "las la-user",
    lastName: "las la-user",
    dateOfBirth: "las la-birthday-cake",
    phone: "las la-phone",
    email: "las la-at",
    createdAt: "las la-calendar-plus",
    last_connection: "las la-clock",
    validation: "las la-check-circle",
    enable: "las la-toggle-on",
    subscription_status: "las la-credit-card",
    role: "las la-user-shield",
    id: "las la-id-badge",
    userId: "las la-user",
    address: "las la-map-marker-alt",
    emailVerified: "las la-check-circle", // Corrected from emailVirefied
    updatedAt: "las la-calendar-check", // Added for updatedAt
  };

  // Tab data
  const tabs = [
    { label: "Informations de base", value: "basic" },
    { label: "Contact", value: "contact" },
    { label: "Détails avancés", value: "advanced" },
  ];

  return (
    <Dialog
      open={open}
      handler={() => {}}
      className="w-full max-w-2xl mx-auto transition-all duration-300 bg-white shadow-2xl rounded-2xl dark:bg-gray-900"
    >
      <DialogHeader className="flex items-center justify-center p-6 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-t-2xl">
        <Typography variant="h5" className="font-bold tracking-tight text-center text-white animate-fade-in">
          Détails du Client
        </Typography>
      </DialogHeader>
      <DialogBody className="p-6 max-h-[70vh] overflow-y-auto">
        <Tabs value={activeTab} onChange={(value: any) => setActiveTab(value as string)} className="w-full">
          <TabsHeader className="p-1 bg-gray-100 rounded-lg dark:bg-gray-800">
            {tabs.map(({ label, value }) => (
              <Tab key={value} value={value} className="p-2 text-sm font-semibold">
                {label}
              </Tab>
            ))}
          </TabsHeader>
          <TabsBody className="mt-4">
            {/* Basic Information Tab */}
            <TabPanel value="basic" className="space-y-4">
              {[
                { label: "Nom", value: clientData?.name, icon: fieldIcons.name },
                { label: "Prénom", value: clientData?.lastName, icon: fieldIcons.lastName },
                {
                  label: "Date de naissance",
                  value: clientData?.dateOfBirth
                    ? new Date(clientData.dateOfBirth).toLocaleDateString("fr-FR")
                    : "N/A",
                  icon: fieldIcons.dateOfBirth,
                },
                {
                  label: "Statut de validation",
                  value: clientData?.validation || "N/A",
                  icon: fieldIcons.validation,
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 transition-shadow duration-200 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800 hover:shadow-md"
                >
                  <div className="flex items-center space-x-2">
                    <i className={`${item.icon} text-xl text-blue-500 dark:text-blue-400`}></i>
                    <Label className="font-semibold text-gray-700 dark:text-gray-200">{item.label} :</Label>
                  </div>
                  <Typography className="font-medium text-gray-900 dark:text-white">
                    {item.value || "N/A"}
                  </Typography>
                </div>
              ))}

              {/* Collapsible Address */}
              <div className="mt-4">
                <Button
                  variant="text"
                  className="flex items-center gap-2 text-blue-600 dark:text-blue-400"
                  onClick={() => toggleCollapse("address")}
                >
                  <i className={fieldIcons.address}></i>
                  Adresse {openCollapse.address ? "▲" : "▼"}
                </Button>
                <Collapse open={openCollapse.address}>
                  <div className="p-4 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800">
                    <Typography className="text-gray-700 dark:text-gray-300">
                      {clientData?.line1 ||
                      clientData?.rue ||
                      clientData?.city ||
                      clientData?.country ||
                      clientData?.postal_code
                        ? `${clientData.line1 || ""} ${clientData.rue || ""}, ${clientData.city || ""}, ${clientData.country || ""} ${clientData.postal_code || ""}`.trim()
                        : "Aucune information d'adresse disponible."}
                    </Typography>
                  </div>
                </Collapse>
              </div>
            </TabPanel>

            {/* Contact Information Tab */}
            <TabPanel value="contact" className="space-y-4">
              {[
                { label: "Numéro de téléphone", value: clientData?.phone, icon: fieldIcons.phone },
                { label: "Email", value: clientData?.User?.email, icon: fieldIcons.email },
                {
                  label: "Dernière connexion",
                  value: clientData?.User?.last_connection
                    ? new Date(clientData.User.last_connection).toLocaleDateString("fr-FR", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                        hour: "numeric",
                        minute: "numeric",
                      })
                    : "N/A",
                  icon: fieldIcons.last_connection,
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 transition-shadow duration-200 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800 hover:shadow-md"
                >
                  <div className="flex items-center space-x-2">
                    <i className={`${item.icon} text-xl text-blue-500 dark:text-blue-400`}></i>
                    <Label className="font-semibold text-gray-700 dark:text-gray-200">{item.label} :</Label>
                  </div>
                  <Typography className="font-medium text-gray-900 dark:text-white">
                    {item.value || "N/A"}
                  </Typography>
                </div>
              ))}
            </TabPanel>

            {/* Advanced Details Tab */}
            <TabPanel value="advanced" className="space-y-4">
              {[
                { label: "ID du client", value: clientData?.id, icon: fieldIcons.id },
                { label: "ID de l'utilisateur", value: clientData?.userId, icon: fieldIcons.userId },
                { label: "Rôle", value: clientData?.User?.role, icon: fieldIcons.role },
                {
                  label: "Statut de l'abonnement",
                  value: clientData?.User?.subscription_status,
                  icon: fieldIcons.subscription_status,
                },
                {
                  label: "Email vérifié",
                  value: clientData?.User?.emailVerified ? "Oui" : "Non", // Corrected from emailVirefied
                  icon: fieldIcons.emailVerified, // Corrected from emailVirefied
                },
                { label: "Compte activé", value: clientData?.enable ? "Oui" : "Non", icon: fieldIcons.enable },
                {
                  label: "Inscrit le",
                  value: clientData?.createdAt
                    ? new Date(clientData.createdAt).toLocaleDateString("fr-FR")
                    : "N/A",
                  icon: fieldIcons.createdAt,
                },
                {
                  label: "Mis à jour le",
                  value: clientData?.updatedAt
                    ? new Date(clientData.updatedAt).toLocaleDateString("fr-FR")
                    : "N/A",
                  icon: fieldIcons.updatedAt,
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 transition-shadow duration-200 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800 hover:shadow-md"
                >
                  <div className="flex items-center space-x-2">
                    <i className={`${item.icon} text-xl text-blue-500 dark:text-blue-400`}></i>
                    <Label className="font-semibold text-gray-700 dark:text-gray-200">{item.label} :</Label>
                  </div>
                  <Typography className="font-medium text-gray-900 dark:text-white">
                    {item.value || "N/A"}
                  </Typography>
                </div>
              ))}
            </TabPanel>
          </TabsBody>
        </Tabs>
      </DialogBody>
      <DialogFooter className="flex justify-center p-4 space-x-4 bg-gray-50 dark:bg-gray-800 rounded-b-2xl">
        <ButtonSecondary
          onClick={handleClose}
          className="px-6 py-2 text-gray-900 transition-colors duration-200 bg-gray-200 rounded-full dark:bg-gray-700 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600"
        >
          Quitter
        </ButtonSecondary>
      </DialogFooter>
    </Dialog>
  );
};

export default ClientDetailsPage;
